import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import {
  ReactElement,
  JSXElementConstructor,
  ReactNode,
  ReactPortal,
  AwaitedReactNode,
} from "react";
import { TrackedLink } from "@/components/common/TrackedLink";
import InlineImage from "@/components/common/InlineImage";

export const createOptions = (
  assetsMap?: Map<string, any>,
  entriesMap?: Map<string, any>,
) => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (
      node: any,
      children:
        | string
        | number
        | bigint
        | boolean
        | ReactElement<any, string | JSXElementConstructor<any>>
        | Iterable<ReactNode>
        | ReactPortal
        | Promise<AwaitedReactNode>
        | null
        | undefined,
    ) => <p>{children}</p>,
    [BLOCKS.TABLE_CELL]: (node: any, children: any) => {
      const className = node?.data?.className || "";
      const colspan = node?.data?.colspan;
      return (
        <td className={className} colSpan={colspan || undefined}>
          {children}
        </td>
      );
    },
    [BLOCKS.TABLE_HEADER_CELL]: (node: any, children: any) => {
      const className = node?.data?.className || "";
      const colspan = node?.data?.colspan;
      return (
        <th className={className} colSpan={colspan || undefined}>
          {children}
        </th>
      );
    },
    [INLINES.HYPERLINK]: (node: any, children: any) => {
      const rawUrl = String(node?.data?.uri ?? "").trim();
      const url =
        !/^mailto:/i.test(rawUrl) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rawUrl)
          ? `mailto:${rawUrl}`
          : rawUrl;

      if (/^https?:\/\//i.test(url)) {
        return (
          <TrackedLink href={url} target="_blank" rel="noopener noreferrer">
            {children}
          </TrackedLink>
        );
      }

      return <TrackedLink href={url}>{children}</TrackedLink>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const assetLink = node?.data?.target;
      // Full asset data comes from the map; the node itself only has a link.
      const asset = assetsMap?.get(assetLink?.sys?.id) || assetLink;

      const url = asset?.fields?.file?.url || asset?.url;
      const title = asset?.fields?.title || asset?.title;
      const description = asset?.fields?.description || asset?.description;

      if (!url) return null;

      return (
        <img
          src={url.startsWith("//") ? `https:${url}` : url}
          alt={description || title || ""}
          className="rich-text-image"
        />
      );
    },
    // The page title is the page's only <h1>; a Heading 1 left in rich text
    // renders as a section heading instead.
    [BLOCKS.HEADING_1]: (_node: any, children: any) => <h2>{children}</h2>,
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const entry = entriesMap?.get(node?.data?.target?.sys?.id);

      if (entry?.__typename === "InlineImage") {
        return <InlineImage {...entry} />;
      }

      return null;
    },
  },
  renderText: (text: string) =>
    text.split("\n").flatMap((text: any, i: number) => [i > 0 && <br />, text]),
});

// Backwards-compatible default options
export const options = createOptions();
